import { useToast } from '@solfacil/girassol'
import { useWhatsApp } from '~/composables/whatsapp'
import { getPartnerArrearsList } from '~/services-v2/solfacil-plus-api/solfacil-plus-api'

const ACCOUNT_MANAGER_WHATSAPP = '5511912386374'
const SOLARINOVE_FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSdW-MaVldKI_sNA0hXL0TksdbCtS1WVFIv8I5ePtxsTLV4Hpw/viewform'

const INSTALLATION_REVIEW_FILTERS = {
  steps: ['installation'],
  status: ['in_progress'],
  order_by: 'created_at:asc',
} as const

const PAYMENT_MODEL_REVIEW_FILTERS = {
  steps: ['payment_model'],
  status: ['in_progress'],
  order_by: 'created_at:asc',
} as const

interface ListingFilters {
  steps?: readonly string[]
  status?: readonly string[]
  order_by?: string
}

export function useBlocklistAlert(partner: Ref<any>, partnerId: Ref<string>) {
  const { t } = useI18n()
  const toast = useToast()
  const { openWhatsApp } = useWhatsApp()
  const router = useRouter()
  const route = useRoute()

  const userIsOnBlockedList = computed(() => {
    const blocklist = partner.value?.blocklist
    if (!blocklist) return false

    if (!blocklist.nature) return false

    if (blocklist.action?.includes('PREVENTIVE_ALERT')) return false

    if (blocklist.nature === 'alert') return true

    return blocklist.is_blocked || false
  })

  function getTranslatedText(key: string | unknown, fallbackKey: string): string {
    if (!key) return t(fallbackKey)
    return t(String(key).toLowerCase())
  }

  const blocklistTitle = computed(() =>
    getTranslatedText(partner.value?.blocklist?.title, 'blocked_list.alert.title'),
  )

  const blocklistDescription = computed(() => {
    const description = getTranslatedText(partner.value?.blocklist?.description, 'blocked_list.alert.description')
    return description.replace(/<[^>]*>/g, '')
  })

  const blocklistType = computed(() => {
    const nature = partner.value?.blocklist?.nature
    if (nature === 'definitive') return 'error'
    if (nature === 'alert' || nature === 'temporary') return 'warning'
    return 'info'
  })

  function navigateToListing(filters: ListingFilters = {}) {
    const query: Record<string, any> = {}

    if (filters.steps?.length) {
      query.steps = filters.steps.join(',')
    }
    if (filters.status?.length) {
      query.status = filters.status.join(',')
    }
    if (filters.order_by) {
      query.order_by = filters.order_by
    }

    router.replace({ path: '/projects', query: { ...route.query, ...query, page: 1 } })
  }

  function navigateToSolarInoveRegistration() {
    window.open(SOLARINOVE_FORM_URL, '_blank', 'noopener,noreferrer')
  }

  async function downloadArrearsList() {
    if (!partnerId.value) return
    try {
      const data = await getPartnerArrearsList(partnerId.value)
      window.open(data?.url, '_blank')
    }
    catch (error) {
      toast.createErrorToast('Erro ao baixar lista de inadimplentes')
      console.error('Failed to download arrears list', error)
    }
  }

  function openAccountManagerWhatsApp() {
    openWhatsApp(ACCOUNT_MANAGER_WHATSAPP, t('blocked_list.whatsapp.message') as string)
  }

  function normalizeActionKey(tag?: string): string | undefined {
    if (!tag) return undefined
    return tag.replace(/^BLOCKED\./, '').replace(/\.ACTION$/, '')
  }

  const blocklistActionHandlers: Record<string, (() => void | Promise<void>) | null> = {
    HIGH_DEFAULT_RATE_ALERT: () => downloadArrearsList(),
    HIGH_DEFAULT_RATE_TEMPORARY: () => downloadArrearsList(),

    DEFAULT_INSTALLATION_DELAY_ALERT: () => navigateToListing(INSTALLATION_REVIEW_FILTERS),
    DEFAULT_INSTALLATION_DELAY_TEMPORARY: () => navigateToListing(INSTALLATION_REVIEW_FILTERS),
    LIMIT_TEMPORARY: () => navigateToListing(INSTALLATION_REVIEW_FILTERS),
    RISK_BUYBACK_ALERT: () => navigateToListing(INSTALLATION_REVIEW_FILTERS),
    RISK_BUYBACK_TEMPORARY: () => navigateToListing(INSTALLATION_REVIEW_FILTERS),

    PENDING_RECEIPT_MODEL_TEMPORARY: () => navigateToListing(PAYMENT_MODEL_REVIEW_FILTERS),

    SOLARINOVE_INTEGRATION_ALERT: navigateToSolarInoveRegistration,

    PAYMENT_BLOCK_BOTS_AMR_ALERT: () => openAccountManagerWhatsApp(),
    PREVENTIVE_ALERT: () => openAccountManagerWhatsApp(),
    PREVENTIVE_TEMPORARY: () => openAccountManagerWhatsApp(),
    DETERIORATED_FINANCIAL_HEALTH_TEMPORARY: () => openAccountManagerWhatsApp(),
    GENERIC: () => openAccountManagerWhatsApp(),

    BLACKLIST_LEGAL_WEBJUD_TEMPORARY: null,
    PARTNERSHIP_TERMINATION_DEFINITIVE: null,
    CONFIRMED_FRAUD_DEFINITIVE: null,
    PARTNER_INACTIVE_TEMPORARY: null,
    DETERIORATED_FINANCIAL_HEALTH_ALERT: () => openAccountManagerWhatsApp(),
    MISSING_OPERATIONAL_AGREEMENT_TEMPORARY: null,
  }

  const hasValidAction = computed(() => {
    const tag = partner.value?.blocklist?.action
    const key = normalizeActionKey(tag)
    if (!key) return false

    const handler = blocklistActionHandlers[key]
    return handler !== null && handler !== undefined
  })

  const blocklistActionLabel = computed(() => {
    if (!hasValidAction.value) return undefined
    return getTranslatedText(partner.value?.blocklist?.action, 'blocked_list.alert.action')
  })

  function handleBlocklistActionClick() {
    if (!hasValidAction.value) return

    const tag = partner.value?.blocklist?.action
    const key = normalizeActionKey(tag)
    if (!key) return

    const handler = blocklistActionHandlers[key]
    if (handler) handler()
  }

  return {
    userIsOnBlockedList,
    blocklistTitle,
    blocklistDescription,
    blocklistType,
    blocklistActionLabel,
    handleBlocklistActionClick,
  }
}
